import { createTheme, PaletteMode, Theme } from "@mui/material";
import { tokens } from "./theme/tokens";

// MUI module augmentation — makes theme.tokens typed throughout the app.
declare module "@mui/material/styles" {
  interface Theme {
    tokens: typeof tokens;
  }
  interface ThemeOptions {
    tokens?: typeof tokens;
  }
}

// Re-export so other modules can import tokens directly from this file.
export { tokens };
export type { Tokens } from "./theme/tokens";

export const isEnterprise = process.env.REACT_APP_IS_ENTERPRISE === "true";
export const enterpriseName = process.env.REACT_APP_ENTERPRISE_NAME ?? "Ommuse";

export const THEME = process.env.REACT_APP_THEME ?? "dark";
export const NAVBAR_AND_FOOTER_BACKGROUND_COLOR =
  process.env.REACT_APP_NAVBAR_AND_FOOTER_BACKGROUND_COLOR ?? "black";
export const APP_BACKGROUND_COLOR =
  process.env.REACT_APP_BACKGROUND_COLOR ?? "#0C111D";
export const APP_COLOR = process.env.REACT_APP_COLOR ?? "#27B6CB";
export const APP_HOVER_COLOR = process.env.REACT_APP_HOVER_COLOR ?? "#1A8899";
export const TEXT_COLOR = process.env.REACT_APP_TEXT_COLOR ?? "#BCBEC0";
export const FONT_FAMILY = process.env.REACT_APP_FONT_FAMILY ?? "Argumentum";
export const SVG_ICON_COLOR = process.env.REACT_APP_SVG_ICON_COLOR ?? "#FFFFFF";
export const NAVBAR_ACTIVE_TEXT_COLOR =
  process.env.REACT_APP_NAVBAR_ACTIVE_TEXT_COLOR ?? APP_COLOR;
export const NAVBAR_FOOTER_TEXT_COLOR =
  process.env.REACT_APP_NAVBAR_FOOTER_TEXT_COLOR ?? "white";
export const BUTTON_TEXT_COLOR =
  process.env.REACT_APP_BUTTON_TEXT_COLOR ?? "white";

export const INITIAL_SETTINGS_BLACK_BACKGROUND_COLOR = "#151718";
export const INITIAL_SETTINGS_BACKGROUND_COLOR = "#0c111d";
export const SKIP_BUTTON_BACKGROUND_COLOR = "#0C111D";
export const SKIP_BUTTON_TEXT_COLOR = "#889096";
export const BORDER_COLOR = "#26292B";

export const ENTERPRISE_IMAGE_PATH =
  `/${process.env.REACT_APP_ENTERPRISE_IMAGE_PATH}` ||
  "/assets/images/logo512.png";
export const ENTERPRISE_ARTWORK_TEMPLATE =
  `/${process.env.REACT_APP_ENTERPRISE_ARTWORK_TEMPLATE_IMAGE_PATH}` ||
  "/assets/images/logo512.png";

export const themeObj = {
  dark: {
    background: {
      default: APP_BACKGROUND_COLOR,
    },
  },
};

export const darkTheme: Theme = (() => {
  const t = createTheme({
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: {
          backgroundColor: APP_BACKGROUND_COLOR,
          fontFamily: FONT_FAMILY,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: APP_BACKGROUND_COLOR,
          fontFamily: FONT_FAMILY,
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          color: APP_BACKGROUND_COLOR,
          fontFamily: FONT_FAMILY,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: APP_BACKGROUND_COLOR,
          fontFamily: FONT_FAMILY,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: APP_BACKGROUND_COLOR,
          fontFamily: FONT_FAMILY,
        },
      },
    },
  },

  palette: {
    mode: THEME as PaletteMode,
    text: {
      primary: TEXT_COLOR,
    },
    ...themeObj,
  },
  typography: {
    fontFamily: FONT_FAMILY,
  },
  });
  t.tokens = tokens;
  return t;
})();

export const lightenDarkenColor: (color: any, amount: any) => string = (
  color: any,
  amount: any
) => {
  let usePound = false;
  if (color[0] === "#") {
    color = color.slice(1);
    usePound = true;
  }
  const num = parseInt(color, 16);
  let r = (num >> 16) + amount;
  if (r > 255) r = 255;
  else if (r < 0) r = 0;
  let b = ((num >> 8) & 0x00ff) + amount;
  if (b > 255) b = 255;
  else if (b < 0) b = 0;
  let g = (num & 0x0000ff) + amount;
  if (g > 255) g = 255;
  else if (g < 0) g = 0;
  return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16);
};
